// src/pages/Casting/MyApplicationsPage.tsx
import { Link } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import StudioShell from '../../components/Studio/StudioShell';
import StudioHero from '../../components/Studio/StudioHero';
import {
  StudioEmptyState,
  StudioErrorState,
} from '../../components/Studio/StudioStates';
import {
  CONTAINER,
  PANEL,
  PANEL_HOVER,
  PRIMARY_BUTTON,
} from '../../components/Studio/studioStyles';
import useStudioResource from '../../hooks/useStudioResource';
import useSEO from '../../hooks/useSEO';
import castingService from '../../services/castingService';

/** How far along a submission is, in plain language. */
const STATUS_TONE: Record<string, string> = {
  SUBMITTED: 'border-white/15 bg-white/5 text-neutral-300',
  UNDER_REVIEW: 'border-sky-500/30 bg-sky-500/10 text-sky-200',
  SHORTLISTED: 'border-amber-500/30 bg-amber-500/10 text-amber-200',
  AUDITION_INVITED: 'border-orange-500/30 bg-orange-500/15 text-orange-200',
  CAST: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200',
  NOT_PROGRESSING: 'border-neutral-600/40 bg-neutral-700/20 text-neutral-400',
  WITHDRAWN: 'border-neutral-600/40 bg-neutral-700/20 text-neutral-400',
};

function formatSubmitted(value: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const MyApplicationsPage = () => {
  useSEO({
    title: 'My Applications — StrymHub Casting',
    description: 'Track every open call you have applied to on StrymHub Studios productions.',
    type: 'website',
  });

  const applications = useStudioResource(
    (signal) => castingService.getMyApplications(signal),
    [],
  );

  return (
    <StudioShell>
      <StudioHero
        eyebrow="Casting"
        title="Your submissions."
        lede="Every call you have applied to, and where each one stands. We update these as we work through them — no ghosting."
      />

      <div className={`${CONTAINER} py-12`}>
        {applications.loading && (
          <div className="space-y-4" role="status" aria-label="Loading applications">
            {[0, 1, 2].map((index) => (
              <div key={index} className={`${PANEL} h-28 animate-pulse`} />
            ))}
            <span className="sr-only">Loading…</span>
          </div>
        )}

        {!applications.loading && applications.error && (
          <StudioErrorState message={applications.error} onRetry={applications.reload} />
        )}

        {!applications.loading && !applications.error && (applications.data?.length ?? 0) === 0 && (
          <StudioEmptyState
            icon={<Inbox className="h-8 w-8" />}
            title="You haven't applied to anything yet"
            description="Open calls go out as each production moves into casting. Have a look at what's live now."
            action={
              <Link to="/casting" className={PRIMARY_BUTTON}>
                See open calls
              </Link>
            }
          />
        )}

        {!applications.loading && !applications.error && (applications.data?.length ?? 0) > 0 && (
          <ul className="space-y-4">
            {applications.data?.map((application) => {
              const tone = STATUS_TONE[application.status] ?? STATUS_TONE.SUBMITTED;
              const submitted = formatSubmitted(application.submittedAt);

              return (
                <li key={application.id}>
                  <Link
                    to={`/casting/${application.castingCallSlug}`}
                    className={`flex flex-wrap items-center justify-between gap-4 p-6 ${PANEL} ${PANEL_HOVER} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400`}
                  >
                    <div className="min-w-0">
                      <p className="font-display text-base font-bold text-white">
                        {application.castingCallTitle}
                      </p>
                      {application.productionTitle && (
                        <p className="mt-1 text-xs uppercase tracking-wider text-neutral-500">
                          For {application.productionTitle}
                        </p>
                      )}
                      {submitted && (
                        <p className="mt-2 text-sm text-neutral-500">Submitted {submitted}</p>
                      )}
                    </div>

                    <span
                      className={`rounded-full border px-3 py-1 text-xs font-medium ${tone}`}
                    >
                      {application.statusLabel}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </StudioShell>
  );
};

export default MyApplicationsPage;
